import type { FC, ReactNode } from 'react'

import { useRouter } from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

interface RulerMark {
    label: string
    position: number
}

const MINOR_TICKS = 40

/**
 * A slim vertical ruler in the left margin that fills as the page scrolls.
 * Elements carrying data-ruler-mark="05" add a numbered mark at their position.
 */
export const PageRuler: FC = (): ReactNode => {
    const router = useRouter()
    const fillRef = useRef<HTMLSpanElement>(null)
    const labelRef = useRef<HTMLSpanElement>(null)
    const [marks, setMarks] = useState<Array<RulerMark>>([])
    const [active, setActive] = useState(-1)
    const [scrollable, setScrollable] = useState(false)

    useEffect(() => {
        let raf = 0
        let positions: Array<RulerMark> = []

        const layout = (): void => {
            const max = document.documentElement.scrollHeight - window.innerHeight

            setScrollable(max > window.innerHeight * 0.25)

            const elements = Array.from(document.querySelectorAll<HTMLElement>('[data-ruler-mark]'))

            positions = elements.map(element => ({
                label: element.dataset.rulerMark ?? '',
                position: Math.min(1, Math.max(0, (element.getBoundingClientRect().top + window.scrollY - 64) / Math.max(1, max)))
            }))

            setMarks(positions)
        }

        const update = (): void => {
            const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight)
            const progress = Math.min(1, Math.max(0, window.scrollY / max))

            if (fillRef.current) fillRef.current.style.transform = `scaleY(${progress.toFixed(4)})`
            if (labelRef.current) labelRef.current.textContent = String(Math.round(progress * 100)).padStart(3, '0')

            let current = -1

            positions.forEach((mark, index) => {
                if (progress + 0.002 >= mark.position) current = index
            })

            setActive(previous => (previous === current ? previous : current))
        }

        const schedule = (): void => {
            cancelAnimationFrame(raf)
            raf = requestAnimationFrame(update)
        }

        const relayout = (): void => {
            layout()
            schedule()
        }

        const timer = window.setTimeout(relayout, 300)
        const observer = new ResizeObserver(relayout)

        observer.observe(document.body)
        relayout()

        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', relayout)
        router.events.on('routeChangeComplete', relayout)

        return () => {
            window.clearTimeout(timer)
            cancelAnimationFrame(raf)
            observer.disconnect()
            window.removeEventListener('scroll', schedule)
            window.removeEventListener('resize', relayout)
            router.events.off('routeChangeComplete', relayout)
        }
    }, [router.events])

    return (
        <div
            className={cn(
                'page-ruler pointer-events-none fixed bottom-0 left-0 top-14 z-20 w-gutter transition-opacity duration-500 ease-instrument desk:top-16',
                scrollable ? 'opacity-100' : 'opacity-0'
            )}
            aria-hidden='true'
        >
            <div className='absolute inset-y-6 left-1/2 w-px bg-hairline desk:bottom-14'>
                <span ref={fillRef} className='absolute inset-0 origin-top bg-signal' style={{ transform: 'scaleY(0)' }} />

                {Array.from({ length: MINOR_TICKS + 1 }, (_, index) => (
                    <span
                        key={index}
                        className={cn('absolute left-0 h-px -translate-x-full bg-hairline', index % 5 === 0 ? 'w-2' : 'w-1')}
                        style={{ top: `${(index / MINOR_TICKS) * 100}%` }}
                    />
                ))}

                {marks.map((mark, index) => (
                    <span key={`${mark.label}-${index}`} className='absolute left-0' style={{ top: `${mark.position * 100}%` }}>
                        <span className={cn('absolute left-0 h-px w-2.5 -translate-x-1/2', index === active ? 'bg-signal' : 'bg-mute')} />

                        <span className={cn('hud absolute left-2.5 hidden -translate-y-1/2 text-[9px] tracking-[0.06em] desk:block', index === active ? 'text-signal' : 'text-mute')}>
                            {mark.label}
                        </span>
                    </span>
                ))}
            </div>

            <span ref={labelRef} className='hud absolute bottom-4 left-0 hidden w-full text-center text-[9px] tracking-[0.06em] text-mute desk:block'>
                000
            </span>
        </div>
    )
}
